import { Link } from "@shared/client/navigation";
import { ArrowUpRight } from "lucide-react";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { cn } from "@shared/lib/utils";

/**
 * Briques communes aux écrans de contrôle (admin, leads), toutes construites
 * sur shadcn/ui. Charte v2 : pas de grand titre — une ligne méta, une bande de
 * mesures séparées par des filets (pas de cartes à icône teintée), puis des
 * tables denses. Le statut se lit à un point de couleur suivi d'un mot.
 */

export function AdminMeta({ children, note }: { children: React.ReactNode; note?: React.ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-4 text-[13px] text-muted-foreground">
      <span>{children}</span>
      {note && <span className="text-[12px] text-subtle-foreground">{note}</span>}
    </div>
  );
}

// Deux mesures par rangée dès le mobile : une seule colonne étirait une
// bande de quatre chiffres sur la hauteur d'un écran.
const STAT_COLUMNS = {
  2: "grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 xl:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 xl:grid-cols-5",
} as const;

/**
 * Bande de mesures : une seule boîte, des filets d'un pixel entre les
 * cellules. Chaque cellule porte ses bordures droite et basse, et la grille
 * déborde d'un pixel sous le cadre qui les rogne : une rangée incomplète
 * laisse du blanc, jamais une case grise. `bordered={false}` quand la bande
 * vit déjà dans un panneau encadré.
 */
export function StatGroup({
  children,
  columns = 4,
  bordered = true,
  className,
}: {
  children: React.ReactNode;
  columns?: keyof typeof STAT_COLUMNS;
  bordered?: boolean;
  className?: string;
}) {
  return (
    <section className={cn("overflow-hidden", bordered && "rounded-lg border", className)}>
      <div className={cn("-mr-px -mb-px grid", STAT_COLUMNS[columns])}>{children}</div>
    </section>
  );
}

/**
 * Une mesure : libellé au-dessus, valeur, puis une note. Seule une teinte
 * sémantique colore la valeur (`rose` = anomalie) ; les autres teintes
 * historiques restent acceptées mais n'habillent plus rien.
 */
export function StatCard({
  value,
  label,
  note,
  tint,
}: {
  /** Conservé pour compatibilité : les icônes décoratives ne sont plus rendues. */
  icon?: React.ReactNode;
  value: React.ReactNode;
  label: string;
  note?: React.ReactNode;
  tint?: "blue" | "teal" | "amber" | "green" | "rose";
}) {
  return (
    <div className="min-w-0 border-r border-b px-4 py-3.5 max-sm:px-3 max-sm:py-3">
      <span className="line-clamp-2 block text-[12.5px] text-muted-foreground sm:truncate" title={label}>{label}</span>
      <strong
        className={cn(
          "mt-1.5 block text-[22px] leading-tight font-semibold tracking-[-0.02em] tabular-nums max-sm:text-[19px]",
          tint === "rose" && "text-destructive",
        )}
      >
        {value}
      </strong>
      {note && <small className="mt-0.5 block truncate text-[12px] text-subtle-foreground">{note}</small>}
    </div>
  );
}

export function AdminPanel({
  title,
  action,
  children,
  className,
  flush = false,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  flush?: boolean;
}) {
  return (
    <Card className={cn("gap-0 overflow-hidden py-0", className)}>
      {title && (
        <CardHeader className="flex flex-row items-center justify-between gap-3.5 border-b px-4 py-3 [.border-b]:pb-3">
          <CardTitle className="text-[13px] font-semibold">{title}</CardTitle>
          {action && <CardAction>{action}</CardAction>}
        </CardHeader>
      )}
      <CardContent className={cn(flush ? "px-0" : "px-4 py-4")}>{children}</CardContent>
    </Card>
  );
}

export function PanelLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      className="inline-flex items-center gap-1 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      href={href}
    >
      {children} <ArrowUpRight size={13} aria-hidden="true" />
    </Link>
  );
}

const DOT_TONES: Record<string, string> = {
  active: "bg-success",
  success: "bg-success",
  trial: "bg-warning",
  warning: "bg-warning",
  churned: "bg-destructive",
  danger: "bg-destructive",
  role: "bg-primary",
  info: "bg-primary",
  lead: "bg-subtle-foreground",
  neutral: "bg-subtle-foreground",
  ink: "bg-foreground",
};

/** Point de couleur + libellé : l'état se lit sans la couleur, et sans pastille. */
export function StatusDot({
  tone = "neutral",
  className,
  children,
}: {
  tone?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[12.5px] whitespace-nowrap text-muted-foreground", className)}>
      <i aria-hidden="true" className={cn("size-1.5 shrink-0 rounded-full", DOT_TONES[tone] ?? DOT_TONES.neutral)} />
      {children}
    </span>
  );
}

/** Ancien nom de `StatusDot`, conservé pour les écrans qui l'importent encore. */
export const AdminChip = StatusDot;

export function AdminEmpty({ children }: { children: React.ReactNode }) {
  return <p className="m-0 px-4 py-10 text-center text-[13px] text-muted-foreground">{children}</p>;
}
